export const AnalyticsEvents = {
  FlowStepEntered: 'flow_step_entered',
  FlowStepCompleted: 'flow_step_completed',
  IntroStarted: 'intro_started',
  IntroCompleted: 'intro_completed',
  PersonalityTestStarted: 'personality_test_started',
  PersonalityTestCompleted: 'personality_test_completed',
  PersonalityTestSkipped: 'personality_test_skipped',
  CoachConfigured: 'coach_configured',
  ChatMessageSent: 'chat_message_sent',
  ChatMessageSentServer: 'chat_message_sent_server',
  WorkoutStarted: 'workout_started',
  WorkoutCompleted: 'workout_completed',
  DashboardModeOpened: 'dashboard_mode_opened',
  PushSubscribed: 'push_subscribed',
  PushTestSent: 'push_test_sent',
  SafeExitTriggered: 'safe_exit_triggered',
  CovenantConfirmed: 'covenant_confirmed',
  UpdatePrompted: 'update_prompted',
  UpdateApplied: 'update_applied',
} as const;

export type AnalyticsEvent = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

export const AnalyticsProps = {
  StepName: 'step_name',
  DurationMs: 'duration_ms',
  Gender: 'gender',
  Tone: 'tone',
  CoachName: 'coach_name',
  Mode: 'mode',
  Level: 'level',
  PointsAwarded: 'points_awarded',
  MessageLength: 'message_length',
  IpHash: 'ip_hash',
  UserAgent: 'user_agent',
  /** 'chip' (suggestion picked) | 'custom' (user typed own) | 'none' (skip or empty submit). */
  SafeWordSource: 'safe_word_source',
  /** Numeric count chosen on the ritual screen (e.g. 3 for "3× per week"). Null when skipped. */
  RitualCount: 'ritual_count',
  /** 'day' | 'week' — the period unit the count is multiplied by. Null when skipped. */
  RitualPeriod: 'ritual_period',
  /** 'soft' | 'challenging' | 'push' — preset chip that seeded the values, or null if hand-picked. */
  RitualIntensity: 'ritual_intensity',
  /** True when the user tapped Skip on the ritual screen instead of confirming. */
  RitualSkipped: 'ritual_skipped',
  /** 'completed' (all 10 answered) | 'skipped_intro' (skipped before Q1) | 'skipped_mid' (started, abandoned). */
  PersonalityTestOutcome: 'personality_test_outcome',
  /** Number of questions answered before submit or skip (0 if skipped at intro). */
  PersonalityTestAnswered: 'personality_test_answered',
  PersonalityScoreOpenness: 'personality_openness',
  PersonalityScoreConscientiousness: 'personality_conscientiousness',
  PersonalityScoreExtraversion: 'personality_extraversion',
  PersonalityScoreAgreeableness: 'personality_agreeableness',
  PersonalityScoreNeuroticism: 'personality_neuroticism',
} as const;

export type AnalyticsProp = (typeof AnalyticsProps)[keyof typeof AnalyticsProps];

export const FlowSteps = {
  IntroScreen1: 'intro:screen_1',
  IntroScreen2: 'intro:screen_2',
  IntroScreen3: 'intro:screen_3',
  IntroScreen4: 'intro:screen_4',
  IntroScreen5: 'intro:screen_5',
  CoachConfiguratorGender: 'coach_configurator:gender',
  CoachConfiguratorTone: 'coach_configurator:tone',
  CoachConfiguratorLook: 'coach_configurator:look',
  CoachConfiguratorCovenant: 'coach_configurator:covenant',
  /** Legacy — no longer fired since the safeword step moved into the intro flow. */
  CoachConfiguratorSafeword: 'coach_configurator:safeword',
  CoachConfiguratorGenerating: 'coach_configurator:generating',
  CoachConfiguratorReveal: 'coach_configurator:reveal',
} as const;

export type FlowStep = (typeof FlowSteps)[keyof typeof FlowSteps];
